import { motion } from 'framer-motion';

const STEPS = [
  { key:'SOUMIS',                  short:'Soumis',    label:'Dossier soumis',      desc:'Reçu et enregistré',                   icon:'📋', color:'#c8902a', light:'rgba(200,144,42,.18)', bg:'rgba(200,144,42,.08)' },
  { key:'VERIFICATION_CADASTRALE', short:'Cadastre',  label:'Vérif. cadastrale',   desc:'Plan cadastral et limites parcellaires', icon:'🗺️', color:'#7c6bbf', light:'rgba(124,107,191,.18)', bg:'rgba(124,107,191,.07)' },
  { key:'ENQUETE_TERRAIN',         short:'Terrain',   label:'Enquête terrain',     desc:'Constat physique sur place',            icon:'🔍', color:'#c2521a', light:'rgba(194,82,26,.18)',  bg:'rgba(194,82,26,.07)'  },
  { key:'VERIFICATION_TITRES',     short:'Titres',    label:'Vérif. des titres',   desc:'Chaîne de propriété et documents',      icon:'📜', color:'#2a8fa8', light:'rgba(42,143,168,.18)', bg:'rgba(42,143,168,.07)' },
  { key:'RAPPORT_EXPERTISE',       short:'Rapport',   label:"Rapport d'expertise", desc:'Synthèse certifiée remise au client',   icon:'📊', color:'#2f6645', light:'rgba(47,102,69,.18)', bg:'rgba(47,102,69,.08)'  },
  { key:'CLOTURE',                 short:'Clôturé',   label:'Dossier clôturé',     desc:'Résultat final définitif',              icon:'🔒', color:'#244d35', light:'rgba(36,77,53,.18)',  bg:'rgba(36,77,53,.07)'   },
];
const ORDER = STEPS.map(s => s.key);

function getStatus(key, statut, etapeTerminee) {
  const ci = ORDER.indexOf(statut), si = ORDER.indexOf(key);
  if (si < ci) return 'done';
  if (si === ci) return etapeTerminee ? 'waiting' : 'active';
  return 'pending';
}

const css = `
.st { width:100%; font-family:'DM Sans',sans-serif; }

/* track */
.st-track { display:flex; align-items:flex-start; padding:0 2px; }
.st-node  { display:flex; flex-direction:column; align-items:center; gap:7px; flex:none; width:60px; }

/* connector */
.st-conn {
  flex:1; height:3px; border-radius:999px;
  background:rgba(26,61,40,.08);
  margin-top:16px; position:relative; overflow:hidden;
}
.st-conn-fill {
  position:absolute; inset:0; border-radius:999px;
  transform-origin:left;
}

/* circle */
.st-circle {
  width:32px; height:32px; border-radius:50%;
  display:grid; place-items:center;
  border:2.5px solid transparent;
  position:relative; flex:none;
  transition:all .35s ease;
  font-size:.88rem;
}
.st-circle.done   { box-shadow:0 4px 12px rgba(0,0,0,.15); }
.st-circle.active { box-shadow:0 6px 16px rgba(0,0,0,.18); }
.st-circle.waiting{ animation:st-blink 1.8s ease-in-out infinite; }
.st-circle.pending{ background:rgba(26,61,40,.04)!important; border-color:rgba(26,61,40,.12)!important; }

/* pulse ring */
.st-pulse {
  position:absolute; inset:-6px; border-radius:50%;
  animation:st-pulse-anim 2s ease-in-out infinite;
}
@keyframes st-pulse-anim { 0%,100%{transform:scale(1);opacity:.5;} 50%{transform:scale(1.5);opacity:0;} }
@keyframes st-blink { 0%,100%{box-shadow:0 0 0 0 rgba(200,144,42,.6);} 50%{box-shadow:0 0 0 8px rgba(200,144,42,0);} }

/* checkmark */
.st-chk { position:relative; width:11px; height:8px; }
.st-chk::before,.st-chk::after { content:''; position:absolute; background:white; border-radius:2px; }
.st-chk::before { width:4px;height:2px;bottom:0;left:0;transform:rotate(45deg);transform-origin:bottom left; }
.st-chk::after  { width:8px;height:2px;bottom:1.5px;left:2px;transform:rotate(-45deg);transform-origin:bottom left; }

/* label */
.st-short { font-size:.65rem; font-weight:700; text-align:center; line-height:1.25; transition:color .3s; letter-spacing:.01em; }

/* active detail */
.st-detail {
  margin-top:16px; border-radius:14px; padding:14px 16px;
  display:flex; align-items:center; gap:12px;
  border:1.5px solid transparent; transition:all .4s ease;
}
.st-dicon { width:44px;height:44px;border-radius:12px;display:grid;place-items:center;font-size:1.3rem;flex:none; }
.st-deyebrow { font-size:.62rem;font-weight:800;letter-spacing:.18em;text-transform:uppercase;opacity:.55;margin-bottom:3px; }
.st-dtitle { font-family:'Cormorant Garamond',serif; font-size:1.05rem;font-weight:700;line-height:1.1; }
.st-ddesc  { font-size:.75rem;opacity:.6;margin-top:2px;line-height:1.5; }
.st-wpill  {
  margin-left:auto; display:inline-flex; align-items:center; gap:5px;
  padding:5px 11px; border-radius:999px; font-size:.68rem; font-weight:700;
  background:rgba(200,144,42,.15); color:#8a6010; white-space:nowrap; flex:none;
  animation:st-blink 1.8s ease-in-out infinite;
}

/* legend */
.st-legend { display:flex; align-items:center; gap:14px; margin-top:12px; flex-wrap:wrap; }
.st-leg-item { display:flex; align-items:center; gap:5px; font-size:.68rem; color:rgba(110,128,112,.55); }
.st-leg-dot  { width:7px;height:7px;border-radius:50%; }
`;

export default function StepTracker({ dossier }) {
  if (!dossier) return null;
  const { statut, etape_terminee } = dossier;
  const currIdx = ORDER.indexOf(statut);
  const active  = STEPS[currIdx] || STEPS[0];

  return (
    <div className="st">
      <style>{css}</style>

      {/* TRACK */}
      <div className="st-track">
        {STEPS.map((step, i) => {
          const status    = getStatus(step.key, statut, etape_terminee);
          const isDone    = status === 'done';
          const isActive  = status === 'active';
          const isWaiting = status === 'waiting';
          const isPending = status === 'pending';
          const isLast    = i === STEPS.length - 1;

          return (
            <div key={step.key} style={{ display:'flex', alignItems:'flex-start', flex: isLast ? 'none' : 1 }}>
              <div className="st-node">
                <motion.div
                  className={`st-circle ${status}`}
                  style={{
                    background:  isPending ? undefined : step.color,
                    borderColor: isWaiting ? '#c8902a' : 'transparent',
                  }}
                  initial={{ scale:.6, opacity:0 }}
                  animate={{ scale:1,  opacity:1 }}
                  transition={{ delay: i*.065, duration:.4, ease:[.22,1,.36,1] }}>

                  {isActive && (
                    <div className="st-pulse" style={{ border:`2px solid ${step.color}` }}/>
                  )}

                  {isDone    ? <div className="st-chk"/> :
                   isPending ? <span style={{ fontSize:'.66rem', fontWeight:700, color:'rgba(26,61,40,.25)' }}>{i+1}</span> :
                               <span style={{ fontSize:'.82rem', lineHeight:1 }}>{step.icon}</span>}
                </motion.div>

                <motion.span className="st-short"
                  style={{ color: isPending ? 'rgba(110,128,112,.38)' : step.color }}
                  initial={{ opacity:0 }} animate={{ opacity:1 }}
                  transition={{ delay: i*.065+.1 }}>
                  {step.short}
                </motion.span>
              </div>

              {!isLast && (
                <div className="st-conn">
                  <motion.div className="st-conn-fill"
                    style={{ background:`linear-gradient(90deg,${STEPS[i].color},${STEPS[i+1].color})` }}
                    initial={{ scaleX:0 }}
                    animate={{ scaleX: isDone ? 1 : 0 }}
                    transition={{ delay: i*.065+.2, duration:.55, ease:[.22,1,.36,1] }}/>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ACTIVE DETAIL */}
      <motion.div className="st-detail"
        key={statut}
        style={{ background: active.bg, borderColor: active.light, color: active.color }}
        initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
        transition={{ delay:.45, duration:.45, ease:[.22,1,.36,1] }}>
        <div className="st-dicon" style={{ background: active.light }}>{active.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div className="st-deyebrow" style={{ color: active.color }}>Étape en cours</div>
          <div className="st-dtitle"   style={{ color: active.color }}>{active.label}</div>
          <div className="st-ddesc"    style={{ color: active.color }}>{active.desc}</div>
        </div>
        {etape_terminee && <div className="st-wpill">⏳ En attente</div>}
      </motion.div>

      {/* LEGEND */}
      <div className="st-legend">
        {[
          { dot: '#2f6645',                  label:'Complété' },
          { dot: active.color,               label:'En cours', pulse:true },
          { dot: 'rgba(26,61,40,.14)',        label:'À venir' },
        ].map(({ dot, label, pulse }) => (
          <div key={label} className="st-leg-item">
            <div className="st-leg-dot" style={{ background:dot, ...(pulse?{animation:'st-blink 1.8s ease-in-out infinite'}:{}) }}/>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}